export type Severity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

export type Action = 'escalate' | 'investigate' | 'dismiss' | 'tune';

export type SourceType =
  | 'scanner'
  | 'tor'
  | 'residential'
  | 'cloud'
  | 'internal'
  | 'partner';

export type AssetRole =
  | 'web'
  | 'db'
  | 'admin'
  | 'honeypot'
  | 'workstation'
  | 'jumpbox'
  | 'backup';

export type AlertCategory =
  | 'brute-force'
  | 'malware'
  | 'data-exfil'
  | 'privilege-escalation'
  | 'lateral-movement'
  | 'reconnaissance'
  | 'policy-violation';

export interface SourceContext {
  ip: string;
  type: SourceType;
  hostname?: string;
  geo?: string;
  threatIntel?: string;
  isKnownBad: boolean;
  isKnownGood: boolean;
}

export interface TargetContext {
  hostname: string;
  ip: string;
  role: AssetRole;
  owner?: string;
  criticality: 'low' | 'medium' | 'high' | 'critical';
  isPublic: boolean;
}

export interface RelatedAlert {
  id: string;
  title: string;
  time: string;
  severity: Severity;
}

export interface AlertContext {
  source: SourceContext;
  target: TargetContext;
  relatedAlerts: RelatedAlert[];
  baselineNote?: string;
  threatIntelNote?: string;
  ticketReference?: string;
  chainScope?: {
    source: SourceContext;
    target: TargetContext;
  };
}

export interface Alert {
  id: string;
  title: string;
  severity: Severity;
  category: AlertCategory;
  timestamp: string;
  summary: string;
  details: Record<string, string | number | string[]>;
  context: AlertContext;
  correctAction: Action;
  explanation: string;
  attackChainId?: string;
  attackChainStep?: number;
  attackChainTotal?: number;
  mitreTechnique?: string;
}

export type AttackChainPhase =
  | 'recon'
  | 'initial-access'
  | 'execution'
  | 'persistence'
  | 'priv-esc'
  | 'lateral-movement'
  | 'exfil';

export interface AttackChainDefinition {
  id: string;
  name: string;
  phases: AttackChainPhase[];
  templateIds: string[]; // Alert template IDs for each phase
}

export type Difficulty = 'intern' | 'analyst' | 'senior' | 'burnout';

export interface GameConfig {
  difficulty: Difficulty;
  alertCount: number;
  timeLimit: number; // seconds per alert, 0 = unlimited
  seed?: number;
}

export interface PlayerDecision {
  alertId: string;
  action: Action;
  timeSpent: number; // milliseconds
  timestamp: number;
}

export interface RoundResult {
  alertId: string;
  playerAction: Action;
  correctAction: Action;
  isCorrect: boolean;
  points: number;
  timeSpent: number;
  explanation: string;
}

export interface GameStats {
  totalAlerts: number;
  correctCount: number;
  incorrectCount: number;
  totalPoints: number;
  streak: number;
  maxStreak: number;
  precision: number; // of escalations, how many were real
  recall: number; // of real attacks, how many were escalated
  avgTimeMs: number;
  missedAttacks: number;
  falseEscalations: number;
}

export interface GameState {
  config: GameConfig;
  alerts: Alert[];
  currentIndex: number;
  decisions: PlayerDecision[];
  results: RoundResult[];
  stats: GameStats;
  startTime: number;
  isComplete: boolean;
}

export const DIFFICULTY_CONFIG: Record<Difficulty, Omit<GameConfig, 'seed'>> = {
  intern: {
    difficulty: 'intern',
    alertCount: 10,
    timeLimit: 0,
  },
  analyst: {
    difficulty: 'analyst',
    alertCount: 20,
    timeLimit: 30,
  },
  senior: {
    difficulty: 'senior',
    alertCount: 30,
    timeLimit: 20,
  },
  burnout: {
    difficulty: 'burnout',
    alertCount: 50,
    timeLimit: 15,
  },
};

export const ACTION_LABELS: Record<Action, string> = {
  escalate: 'Escalate',
  investigate: 'Investigate',
  dismiss: 'Dismiss',
  tune: 'Tune Out',
};

export const SEVERITY_COLORS: Record<Severity, string> = {
  LOW: 'text-emerald-500',
  MEDIUM: 'text-amber-500',
  HIGH: 'text-orange-500',
  CRITICAL: 'text-rose-500',
};
